import { useState } from "react";

import { INotification } from "shared/components/organisms/AppNotification/@types";
import api from "shared/infra/services/api";

import { captureException, captureMessage } from "@sentry/nextjs";

export const useUserNotificationsService = () => {
  const [isNotificationsLoading, setIsNotificationsLoading] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    perPage: 10,
    isLastPage: false,
  });

  const getNotifications = async (): Promise<INotification<any>[]> => {
    try {
      setIsNotificationsLoading(true);

      const { data } = await api.get("/user-notifications", {
        params: {
          page: paginationInfo?.page,
          perPage: paginationInfo?.perPage,
        },
      });

      setPaginationInfo((prev) => ({
        ...prev,
        page: prev?.page + 1,
        isLastPage: prev?.page + 1 > data?.meta?.last_page,
      }));

      return data?.data;
    } catch (error) {
      console.log("Error (getNotifications): ", error);
      captureMessage("Error (getNotifications)");
      captureException(error);
    } finally {
      setIsNotificationsLoading(false);
    }
  };

  const getNewNotifications = async (): Promise<INotification<any>[]> => {
    try {
      setIsNotificationsLoading(true);

      const { data } = await api.get("/user-notifications", {
        params: {
          page: 1,
          perPage: paginationInfo?.perPage,
        },
      });

      const result: INotification<any>[] = data?.data;

      return result?.filter((notification) => !notification?.read_at);
    } catch (error) {
      console.log("Error (getNewNotifications): ", error);
      captureMessage("Error (getNewNotifications)");
      captureException(error);
    } finally {
      setIsNotificationsLoading(false);
    }
  };

  const markAllNotificationsAsRead = async (): Promise<boolean> => {
    try {
      await api.put("/user-notifications/read-all");

      return true;
    } catch (error) {
      console.log("Error (markAllNotificationsAsRead): ", error);
      captureMessage("Error (markAllNotificationsAsRead)");
      captureException(error);

      return false;
    }
  };

  const verifyIfHasNewNotifications = async (): Promise<boolean> => {
    try {
      const { data } = await api.get("/user-notifications/has-unread");

      const hasNewNotifications = data?.attrs?.has_unread_notifications;

      return hasNewNotifications;
    } catch (error) {
      console.log("Error (verifyIfHasNewNotifications): ", error);
      captureMessage("Error (verifyIfHasNewNotifications)");
      captureException(error);

      return false;
    }
  };

  return {
    getNotifications,
    isNotificationsLoading,

    markAllNotificationsAsRead,

    verifyIfHasNewNotifications,
    paginationInfo,

    getNewNotifications,
  };
};
